import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Plus } from 'lucide-react';
import type { Pharmacy } from '@quds/shared';
import { Badge, Button, ConfirmDialog, Modal, NotLinkedNotice, SecretField, Stepper, type StepDefinition } from '@/components/ui';
import { createPharmacy, provisionBusiness, type ProvisionResponse } from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { PharmacyInfoFields } from '../components/PharmacyInfoFields';
import { hasErrors, type FieldErrors, type PharmacyInfoValues } from '../validation';
import { BranchStep, OptionalServicesStep, ReviewStep, SystemStep } from './WizardSteps';
import {
  initialWizardState,
  mapServerIssues,
  toCreateInput,
  toProvisionInput,
  validateBranch,
  validateLicense,
  validatePharmacyInfo,
  type BranchErrors,
  type LicenseErrors,
  type WizardState,
} from './wizardModel';

const STEPS: readonly StepDefinition[] = [
  { id: 'info', label: 'Business Information' },
  { id: 'branch', label: 'Branches & Location' },
  { id: 'system', label: 'System Setup' },
  { id: 'optional', label: 'Optional Services' },
  { id: 'review', label: 'Review & Create' },
];

interface Errors {
  info: FieldErrors<PharmacyInfoValues>;
  branch: BranchErrors;
  license: LicenseErrors;
}

const noErrors = (): Errors => ({ info: {}, branch: {}, license: {} });

interface AddPharmacyWizardProps {
  onClose: () => void;
}

/** Mount only while open; state resets on every mount. */
export function AddPharmacyWizard({ onClose }: AddPharmacyWizardProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(initialWizardState);
  const [errors, setErrors] = useState<Errors>(noErrors);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<Pharmacy | null>(null);
  const [provisionResult, setProvisionResult] = useState<ProvisionResponse | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const dirty = JSON.stringify(state) !== JSON.stringify(initialWizardState());

  const reset = () => {
    setStep(0);
    setState(initialWizardState());
    setErrors(noErrors());
    setSubmitError(null);
    setCreated(null);
    setProvisionResult(null);
  };

  useEffect(() => {
    if (created) return;
    const t = window.setTimeout(() => {
      bodyRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button[role="combobox"]')?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [step, created]);

  const requestClose = () => {
    if (submitting) return;
    if (!created && dirty) setConfirmDiscard(true);
    else onClose();
  };

  const validateStep = (index: number): boolean => {
    if (index === 0) {
      const info = validatePharmacyInfo(state.info);
      setErrors((e) => ({ ...e, info }));
      return !hasErrors(info);
    }
    if (index === 1) {
      const branch = validateBranch(state.branch);
      setErrors((e) => ({ ...e, branch }));
      return !hasErrors(branch);
    }
    if (index === 3) {
      const license = validateLicense(state.license);
      setErrors((e) => ({ ...e, license }));
      return !hasErrors(license);
    }
    return true;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    for (const i of [0, 1, 3]) {
      if (!validateStep(i)) {
        setStep(i);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (state.production.createProductionEnvironment) {
        const res = await provisionBusiness(toProvisionInput(state));
        setProvisionResult(res);
        setCreated(res.pharmacy);
      } else {
        const pharmacy = await createPharmacy(toCreateInput(state));
        setCreated(pharmacy);
      }
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) {
        const mapped = mapServerIssues(err.fieldIssues);
        setErrors({ info: mapped.info, branch: mapped.branch, license: mapped.license });
        setStep(mapped.step);
      }
      setSubmitError(errorMessage(err, 'Could not create the business.'));
    } finally {
      setSubmitting(false);
    }
  };

  const patchInfo = (patch: Partial<PharmacyInfoValues>) => {
    setState((s) => {
      const info = { ...s.info, ...patch };
      if (hasErrors(errors.info)) setErrors((e) => ({ ...e, info: validatePharmacyInfo(info) }));
      return { ...s, info };
    });
  };
  const patchBranch = (patch: Partial<WizardState['branch']>) => {
    setState((s) => {
      const branch = { ...s.branch, ...patch };
      if (hasErrors(errors.branch)) setErrors((e) => ({ ...e, branch: validateBranch(branch) }));
      return { ...s, branch };
    });
  };
  const patchLicense = (patch: Partial<WizardState['license']>) => {
    setState((s) => {
      const license = { ...s.license, ...patch };
      if (hasErrors(errors.license)) setErrors((e) => ({ ...e, license: validateLicense(license) }));
      return { ...s, license };
    });
  };

  if (created) {
    return (
      <Modal open onClose={onClose} size="md" title="Add Business">
        <div className="flex flex-col items-center py-6 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckCircle2 size={28} aria-hidden="true" />
          </span>
          <h3 className="mt-4 text-lg font-semibold text-ink" role="status">
            Business Created Successfully
          </h3>
          <p className="mt-2 max-w-sm text-sm text-ink-secondary">
            <strong className="text-ink">{created.name}</strong> was saved to Central Admin as{' '}
            <span className="font-mono">{created.code}</span>.
          </p>
          {provisionResult?.createdProduction ? (
            <ProvisionSummary result={provisionResult} />
          ) : (
            <NotLinkedNotice title="Not connected." className="mt-5 text-left">
              No production tenant, branch, API key, license, device enrollment, updater customer or owner account was created.
            </NotLinkedNotice>
          )}
          <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              data-autofocus
              onClick={() => {
                onClose();
                navigate(`/pharmacies/${created.id}`);
              }}
            >
              View Business
            </Button>
            <Button variant="secondary" leftIcon={<Plus size={16} aria-hidden="true" />} onClick={reset}>
              Add Another
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                onClose();
                navigate('/pharmacies');
              }}
            >
              Back to Pharmacies
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const isLast = step === STEPS.length - 1;

  return (
    <>
      <Modal
        open
        onClose={requestClose}
        size="lg"
        title="Add Business"
        description={`Pharmacy · Step ${step + 1} of ${STEPS.length} · ${STEPS[step]!.label}`}
        dismissible={!submitting}
        footer={
          <>
            {step === 0 ? (
              <Button variant="secondary" onClick={requestClose}>
                Cancel
              </Button>
            ) : (
              <Button
                variant="secondary"
                leftIcon={<ArrowLeft size={16} aria-hidden="true" />}
                onClick={() => setStep((s) => s - 1)}
                disabled={submitting}
              >
                Back
              </Button>
            )}
            {isLast ? (
              <Button onClick={submit} loading={submitting}>
                Create Business
              </Button>
            ) : (
              <Button rightIcon={<ArrowRight size={16} aria-hidden="true" />} onClick={next}>
                Next
              </Button>
            )}
          </>
        }
      >
        <Stepper steps={STEPS} current={step} className="mb-6" />
        {submitError && (
          <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
            {submitError}
          </p>
        )}
        <div
          ref={bodyRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLast && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              next();
            }
          }}
        >
          {step === 0 && (
            <PharmacyInfoFields values={state.info} errors={errors.info} onChange={patchInfo} nameLabel="Business Name" />
          )}
          {step === 1 && (
            <BranchStep branch={state.branch} errors={errors.branch} onChange={patchBranch} info={state.info} />
          )}
          {step === 2 && (
            <SystemStep
              production={state.production}
              onProductionChange={(patch) => setState((s) => ({ ...s, production: { ...s.production, ...patch } }))}
            />
          )}
          {step === 3 && (
            <OptionalServicesStep
              services={state.services}
              onServicesChange={(patch) => setState((s) => ({ ...s, services: { ...s.services, ...patch } }))}
              production={state.production}
              onProductionChange={(patch) => setState((s) => ({ ...s, production: { ...s.production, ...patch } }))}
              license={state.license}
              licenseErrors={errors.license}
              onLicenseChange={patchLicense}
            />
          )}
          {step === 4 && <ReviewStep state={state} onEdit={setStep} />}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard new business?"
        confirmLabel="Discard"
        message="The information you entered will be lost."
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
      />
    </>
  );
}

/** One-time secrets and step checklist after a Production provisioning run. */
function ProvisionSummary({ result }: { result: ProvisionResponse }) {
  const status = result.provisioning?.status ?? 'COMPLETED';
  const secrets = result.secrets;
  const partial = status !== 'COMPLETED';
  const steps = result.provisioning?.steps ?? [];

  return (
    <div className="mt-5 w-full space-y-3 text-left">
      <div className="flex items-center gap-2">
        <Badge tone={partial ? 'warning' : 'success'} dot>
          {status.replaceAll('_', ' ')}
        </Badge>
        {result.tenantId && <span className="text-[13px] text-ink-secondary">Tenant created</span>}
      </div>

      <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning">
        Enrollment codes and passwords below are shown once. Copy them before closing.
      </div>

      {secrets?.enrollmentCodes?.length ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-ink-muted">POS enrollment codes</p>
          {secrets.enrollmentCodes.map((c) => (
            <SecretField
              key={c.branchRef}
              label={`Enrollment · ${c.branchRef}`}
              value={c.enrollmentCode}
              initiallyRevealed
              notRecoverable
            />
          ))}
        </div>
      ) : null}

      {secrets?.apiKeyPlaintext && (
        <SecretField label="API key" value={secrets.apiKeyPlaintext} initiallyRevealed notRecoverable />
      )}

      {secrets?.ownerTemporaryPassword && (
        <SecretField
          label={`Owner temp password (${secrets.ownerUsername})`}
          value={secrets.ownerTemporaryPassword}
          initiallyRevealed
          notRecoverable
        />
      )}

      {partial && steps.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-ink-muted">Steps</p>
          <ul className="space-y-1 text-[13px]">
            {steps.map((s) => (
              <li key={s.key} className="flex items-center justify-between">
                <span className="text-ink-secondary">{s.key}</span>
                <span
                  className={
                    s.status === 'failed' ? 'text-danger' : s.status === 'completed' ? 'text-success' : 'text-ink-muted'
                  }
                >
                  {s.status}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[13px] text-ink-secondary">
            Some steps did not complete. Re-run Production setup from the business details to retry without duplicating
            created resources.
          </p>
        </div>
      )}
    </div>
  );
}
